"use client";

import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";
import { toast } from "@/app/_providers/toast";
import { handleMicrophoneError } from "@utilities/errors";
import { Button } from "@components/core/Button";
import ChevronRight16 from "@ui/icons/16x16/chevron-right-16.svg";
import Mic20 from "@ui/icons/20x20/mic-20.svg";
import Spinner20 from "@ui/icons/20x20/spinner-20.svg";
import { cn } from "@ui/lib/utils";
import { useTranslations } from "next-intl";
import { useContext, useEffect, useRef, useState } from "react";

import { STSContext } from "../STS";
import Mic from "../assets/Mic";
import Prompts from "../assets/Prompts";
import Voice from "../assets/Voice";

const MAX_UPLOAD_SIZE = 2 * 1024 * 1024; // 2MB

export default function Overview() {
  const inputRef = useRef<HTMLInputElement>(null);
  const { setView, audioRecorderRef, setFile } = useContext(STSContext);
  const [isLoading, setIsLoading] = useState(false);
  const t = useTranslations("ClientKeys.Globals");
  const tOld = useTranslations("ClientKeys.SpeechToSpeech");
  const tMicrophoneErrors = useTranslations("ClientKeys.Errors.Microphone");

  useEffect(() => {
    if (audioRecorderRef.current === null) return;
    const recorder = audioRecorderRef.current;

    const handleStart = () => {
      setIsLoading(false);
      setView("recording");
    };

    const handleStop = () => setView("generate");

    const handleError = (error: Error) => {
      setIsLoading(false);
      handleMicrophoneError(error, {
        handler: (title, description) => toast.error(title, { description }),
        t: tMicrophoneErrors,
        demoType: "sts",
      });
    };

    recorder.onStart = handleStart;
    recorder.onStop = handleStop;
    recorder.onError = handleError;

    return () => {
      if (recorder.onStart === handleStart) {
        recorder.onStart = null;
      }
      if (recorder.onStop === handleStop) {
        recorder.onStop = null;
      }
      if (recorder.onError === handleError) {
        recorder.onError = null;
      }
    };
  }, [audioRecorderRef, setView, tMicrophoneErrors]);

  function uploadHandler(e: React.ChangeEvent<HTMLInputElement>) {
    if (!e.target.files || e.target.files?.length === 0) return;
    const file = e.target.files[0];
    if (file.size > MAX_UPLOAD_SIZE) {
      captureEvent(EventName.mw_sts_file_upload_error, {
        error_type: "file_too_large",
        file_size_bytes: file.size,
      });
      return toast.error(tOld("widget.error.file_to_large.title"), {
        description: tOld("widget.error.file_to_large.description"),
      });
    }
    captureEvent(EventName.mw_sts_file_uploaded, {
      file_size_bytes: file.size,
      file_type: file.type || file.name.split(".").pop() || "unknown",
    });
    setFile(file);
    setView("generate");
  }

  return (
    <>
      <input
        accept='audio/aac, audio/x-aac, audio/x-aiff, audio/ogg, audio/mpeg, audio/mp3, audio/mpeg3, audio/x-mpeg-3, audio/opus, audio/wav, audio/x-wav, audio/webm, audio/flac, audio/x-flac, audio/mp4'
        className='hidden h-0 w-0'
        onChange={uploadHandler}
        ref={inputRef}
        type='file'
      />
      <div className='flex flex-1 gap-64 pt-72'>
        <div className='flex flex-1 flex-col items-center justify-center rounded-lg'>
          <div className='mb-20 flex items-center justify-center gap-12'>
            <Mic />
            <ChevronRight16 className='text-white' />
            <Prompts />
            <ChevronRight16 className='text-white' />
            <Voice />
          </div>
          <p className='f-description-04 max-w-[300px] text-center'>{tOld("widget.help_text")}</p>
        </div>
      </div>
      <div className='flex justify-between gap-8 p-16 md:ms-auto'>
        <Button onClickCapture={() => inputRef.current?.click()} variant='elevated'>
          <span className='block md:hidden'>{tOld("widget.cta.mobile")}</span>
          <span className='hidden md:block'>{tOld("widget.cta.desktop")}</span>
        </Button>
        <Button
          className='ps-12'
          onClick={() => {
            captureEvent(EventName.mw_sts_recording_started);
            setIsLoading(true);
            audioRecorderRef.current?.startRecording();
          }}
        >
          <span
            className={cn(
              "flex items-center gap-6",
              !isLoading ? "opacity-100" : "opacity-0",
              "transition-opacity duration-300",
            )}
          >
            <Mic20 />
            {t("Start recording")}
          </span>
          <Spinner20
            className={cn("absolute", isLoading ? "opacity-100" : "opacity-0", "transition-opacity duration-300")}
          />
        </Button>
      </div>
    </>
  );
}
